import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/Page/TextLayer.css";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { Input } from "../ui/input";

pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  "pdfjs-dist/build/pdf.worker.min.mjs",
  import.meta.url
).toString();

type PDFViewerProps = {
  fileUrl: string;
  showPager?: boolean;
  defaultPage?: number;
  pageNumber?: number;
  onPageChange?: (page: number) => void;
  enableTextSelection?: boolean;
  onTextSelection?: (text: string) => void;
};

export type PdfViewerRefProps = {
  goToPage: (pageNum: number) => void;
};

const PdfViewer = forwardRef<PdfViewerRefProps, PDFViewerProps>(
  (
    {
      fileUrl,
      defaultPage = 1,
      showPager = true,
      pageNumber,
      onPageChange,
      enableTextSelection = false,
      onTextSelection,
    },
    ref
  ) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const [pageWidth, setPageWidth] = useState<number>();
    const [numPages, setNumPages] = useState<number>(0);
    const [internalPage, setInternalPage] = useState<number>(defaultPage);
    const [inputValue, setInputValue] = useState<string>(String(defaultPage));
    const [pdfData, setPdfData] = useState<Uint8Array | null>(null);

    const currentPage = pageNumber ?? internalPage;
    const isBlobUrl = fileUrl.startsWith("blob:");
    const documentFile = useMemo(
      () => (pdfData ? { data: pdfData } : isBlobUrl ? null : fileUrl),
      [fileUrl, isBlobUrl, pdfData]
    );

    useEffect(() => {
      setPdfData(null);
      if (!isBlobUrl) {
        return;
      }

      let cancelled = false;
      fetch(fileUrl)
        .then((response) => response.arrayBuffer())
        .then((buffer) => {
          if (!cancelled) {
            setPdfData(new Uint8Array(buffer));
          }
        })
        .catch((error) => console.error("PDF Error:", error));

      return () => {
        cancelled = true;
        window.URL.revokeObjectURL(fileUrl);
      };
    }, [fileUrl, isBlobUrl]);

    useEffect(() => {
      setInputValue(String(currentPage));
    }, [currentPage]);

    useEffect(() => {
      const container = containerRef.current;
      if (!container) {
        return;
      }

      const updateWidth = () => {
        setPageWidth(container.clientWidth);
      };

      updateWidth();
      const observer = new ResizeObserver(updateWidth);
      observer.observe(container);
      return () => observer.disconnect();
    }, [fileUrl]);

    useImperativeHandle(ref, () => ({
      goToPage,
    }));

    const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
      setNumPages(numPages);
      if (!pageNumber) {
        setInternalPage(1);
        setInputValue("1");
      }
    };

    const handlePageChange = (change: number) => {
      const newPage = Math.min(Math.max(currentPage + change, 1), numPages);
      if (onPageChange) {
        onPageChange(newPage);
      } else {
        setInternalPage(newPage);
        setInputValue(String(newPage));
      }
    };

    const goToPage = (pageNum: number) => {
      const page = Number(pageNum);
      if (page >= 1 && page <= numPages) {
        if (onPageChange) {
          onPageChange(page);
        } else {
          setInternalPage(page);
          setInputValue(String(page));
        }
      }
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setInputValue(value);

      if (/^\d+$/.test(value)) {
        const newPage = Number(value);
        if (newPage >= 1 && newPage <= numPages) {
          if (onPageChange) {
            onPageChange(newPage);
          } else {
            setInternalPage(newPage);
          }
        }
      }
    };

    const handleTextSelection = () => {
      if (!enableTextSelection || !onTextSelection) return;

      const selection = window.getSelection();
      const container = containerRef.current;
      if (!selection || selection.isCollapsed || !container) return;

      const anchorNode = selection.anchorNode;
      const focusNode = selection.focusNode;
      if (
        (anchorNode && !container.contains(anchorNode)) ||
        (focusNode && !container.contains(focusNode))
      ) {
        return;
      }

      const selectedText = selection.toString().replace(/\s+/g, " ").trim();
      if (selectedText) {
        onTextSelection(selectedText);
      }
    };

    return (
      <div
        ref={containerRef}
        className="flex w-full flex-col items-center gap-4"
        onMouseUp={handleTextSelection}
      >
        {documentFile ? (
          <Document
            file={documentFile}
            onLoadSuccess={onDocumentLoadSuccess}
            onLoadError={(error) => console.error("PDF Error:", error)}
            className="flex w-full justify-center"
          >
            {pageWidth ? (
              <Page
                pageNumber={currentPage}
                width={pageWidth}
                renderAnnotationLayer={false}
                renderTextLayer={enableTextSelection}
              />
            ) : null}
          </Document>
        ) : null}
        {numPages > 0 && showPager && (
          <div className="flex w-full items-center justify-center gap-2 border-t bg-white py-3">
            <Button
              variant="outline"
              size="sm"
              onClick={() => handlePageChange(-1)}
              disabled={currentPage <= 1}
            >
              Previous
            </Button>
            <Input
              value={inputValue}
              onChange={handleInputChange}
              className="w-12"
              placeholder="Page"
            />
            <Label>/ {numPages}</Label>
            <Button
              variant="outline"
              size="sm"
              onClick={() => handlePageChange(1)}
              disabled={currentPage >= numPages}
            >
              Next
            </Button>
          </div>
        )}
      </div>
    );
  }
);

export default PdfViewer;
